/**
 * Service Worker 管理ユーティリティ
 */

export interface ServiceWorkerConfig {
  /** Service Workerファイルのパス */
  swPath?: string
  /** 登録時のスコープ */
  scope?: string
  /** 更新チェック間隔（ミリ秒） */
  updateInterval?: number
  /** デバッグモード */
  debug?: boolean
  /** 更新時のコールバック */
  onUpdate?: (registration: ServiceWorkerRegistration) => void
  /** 成功時のコールバック */
  onSuccess?: (registration: ServiceWorkerRegistration) => void
  /** エラー時のコールバック */
  onError?: (error: Error) => void
}

class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null
  private updateTimer: number | null = null
  private config: Required<ServiceWorkerConfig>

  constructor(config: ServiceWorkerConfig = {}) {
    this.config = {
      swPath: '/sw.js',
      scope: '/',
      updateInterval: 60 * 60 * 1000, // 1時間
      debug: false,
      onUpdate: () => {},
      onSuccess: () => {},
      onError: () => {},
      ...config
    }
  }

  /**
   * Service Workerを登録
   */
  async register(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      console.warn('[SW] Service Worker is not supported');
      return null;
    }

    try {
      if (this.config.debug) {
        console.log('[SW] Registering service worker...');
      }

      this.registration = await navigator.serviceWorker.register(
        this.config.swPath,
        { scope: this.config.scope }
      );

      // 更新チェック
      this.setupUpdateCheck();

      // イベントリスナー設定
      this.setupEventListeners();

      if (this.config.debug) {
        console.log('[SW] Service Worker registered successfully');
      }

      this.config.onSuccess(this.registration);
      
      return this.registration;
    } catch (error) {
      const err = error instanceof Error ? error : new Error('SW registration failed');
      console.error('[SW] Registration failed:', err);
      this.config.onError(err);
      return null;
    }
  }

  /**
   * Service Workerを解除
   */
  async unregister(): Promise<boolean> {
    if (!this.registration) {
      return false;
    }

    try {
      const success = await this.registration.unregister();
      if (success) {
        this.registration = null;
        this.clearUpdateTimer();
        if (this.config.debug) {
          console.log('[SW] Service Worker unregistered');
        }
      }
      return success;
    } catch (error) {
      console.error('[SW] Unregistration failed:', error);
      return false;
    }
  }

  /**
   * 手動で更新チェック
   */
  async checkForUpdate(): Promise<void> {
    if (!this.registration) {
      return;
    }

    try {
      await this.registration.update();
      if (this.config.debug) {
        console.log('[SW] Update check completed');
      }
    } catch (error) {
      console.error('[SW] Update check failed:', error);
    }
  }

  /**
   * キャッシュをクリア
   */
  async clearCache(): Promise<void> {
    if (!('caches' in window)) {
      return;
    }

    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      
      if (this.config.debug) {
        console.log('[SW] All caches cleared');
      }
    } catch (error) {
      console.error('[SW] Cache clear failed:', error);
    }
  }

  /**
   * 特定のURLをプリキャッシュ
   */
  async precache(urls: string[]): Promise<void> {
    if (!this.registration?.active) {
      console.warn('[SW] No active service worker');
      return;
    }

    this.registration.active.postMessage({
      type: 'PRECACHE',
      urls
    });

    if (this.config.debug) {
      console.log('[SW] Precache request sent for', urls.length, 'URLs');
    }
  }

  /**
   * オフライン状態をチェック
   */
  isOffline(): boolean {
    return !navigator.onLine;
  }

  /**
   * 更新チェックのセットアップ
   */
  private setupUpdateCheck(): void {
    this.clearUpdateTimer();
    
    this.updateTimer = window.setInterval(() => {
      this.checkForUpdate();
    }, this.config.updateInterval);
  }

  /**
   * 更新タイマーをクリア
   */
  private clearUpdateTimer(): void {
    if (this.updateTimer !== null) {
      clearInterval(this.updateTimer);
      this.updateTimer = null;
    }
  }

  /**
   * イベントリスナーの設定
   */
  private setupEventListeners(): void {
    if (!this.registration) return;

    // 更新が見つかった時
    this.registration.addEventListener('updatefound', () => {
      const newWorker = this.registration!.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // 新しいバージョンが利用可能
          if (this.config.debug) {
            console.log('[SW] New version available');
          }
          this.config.onUpdate(this.registration!);
        }
      });
    });

    // オンライン/オフライン状態の変更
    window.addEventListener('online', () => {
      if (this.config.debug) {
        console.log('[SW] Back online');
      }
    });

    window.addEventListener('offline', () => {
      if (this.config.debug) {
        console.log('[SW] Gone offline');
      }
    });
  }

  /**
   * 新しいService Workerをアクティベート
   */
  async skipWaiting(): Promise<void> {
    if (!this.registration?.waiting) {
      return;
    }

    this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    
    // ページをリロード
    window.location.reload();
  }
}

// シングルトンインスタンス
let swManager: ServiceWorkerManager | null = null;

/**
 * Service Workerマネージャーを初期化
 */
export function initServiceWorker(config?: ServiceWorkerConfig): ServiceWorkerManager {
  if (!swManager) {
    swManager = new ServiceWorkerManager(config);
  }
  return swManager;
}

/**
 * Service Workerマネージャーを取得
 */
export function getServiceWorker(): ServiceWorkerManager | null {
  return swManager;
}

/**
 * Service Workerを登録（簡易版）
 */
export async function registerServiceWorker(config?: ServiceWorkerConfig): Promise<ServiceWorkerRegistration | null> {
  const manager = initServiceWorker(config);
  return manager.register();
}

/**
 * Service Workerを解除（簡易版）
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if (!swManager) return false;
  return swManager.unregister();
}